import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { saveTournament } from '../../../services/tournament.service';
import {
  TOURNAMENT_TYPE,
  TOURNAMENT_SISTEMA,
  TOURNAMENT_RITMO,
  TOURNAMENT_ESTADO,
  TOURNAMENT_SOFTWARE,
  TOURNAMENT_TIPO_PUNTAJE,
} from '../../../constants/tournament.constants';

export default function StepGeneralData({
  torId,
  initialFormData,
  lastSavedData,
  onSaveSuccess,
  onNextWithoutSaving,
  setAlertInfo,
  type,
}) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        name === 'tipo' ||
        name === 'sistema' ||
        name === 'ritmoJuego' ||
        name === 'nroRondas' ||
        name === 'nroTableros' ||
        name === 'tipoPuntaje' ||
        name === 'estado' ||
        name === 'softwareUsado'
          ? Number(value)
          : value,
    }));
    // Clear error
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre del torneo es obligatorio';
    }
    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es obligatoria';
    }
    if (formData.nroRondas <= 0) {
      newErrors.nroRondas = 'El número de rondas debe ser mayor a 0';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextSubmit = async () => {
    if (!validateForm()) return;

    const isDirty =
      !lastSavedData ||
      Object.keys(formData).some((key) => formData[key] !== lastSavedData[key]);

    if (!isDirty) {
      onNextWithoutSaving();
      return;
    }

    setLoading(true);
    try {
      const isTeams = type === 'teams';
      const payload = {
        ...formData,
        ...(torId ? { torId } : {}),
        // nroTableros solo aplica a torneos por equipos
        nroTableros: isTeams ? formData.nroTableros : undefined,
      };

      const result = await saveTournament(payload);
      if (result && result.tor_id) {
        setAlertInfo({
          open: true,
          message: torId
            ? 'Torneo actualizado exitosamente.'
            : 'Torneo creado exitosamente en la base de datos.',
          severity: 'success',
        });
        onSaveSuccess(result.tor_id, formData);
      } else {
        throw new Error('No se recibió la confirmación del servidor');
      }
    } catch (error) {
      console.error('Error al guardar el torneo:', error);
      setAlertInfo({
        open: true,
        message: 'Error al conectar con el servidor para guardar los datos.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" noValidate>
      <Typography variant="h6" sx={{ color: '#cbd5e1', mb: 2, fontWeight: 600 }}>
        Paso 1: Datos Generales del Torneo
      </Typography>

      <Grid container spacing={3}>
        {/* Nombre */}
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Nombre del Torneo"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            error={!!errors.nombre}
            helperText={errors.nombre}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Organizador */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Organizador"
            name="organizador"
            value={formData.organizador}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Lugar */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Lugar"
            name="lugar"
            value={formData.lugar}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Árbitro */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Árbitro"
            name="arbitro"
            value={formData.arbitro}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Fecha de Inicio */}
        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            type="date"
            label="Fecha de Inicio"
            name="fechaInicio"
            value={formData.fechaInicio}
            onChange={handleChange}
            error={!!errors.fechaInicio}
            helperText={errors.fechaInicio}
            InputLabelProps={{ shrink: true, style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Tipo de Torneo */}
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="Tipo de Torneo"
            name="tipo"
            value={formData.tipo}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            {type === 'teams'
              ? [<MenuItem key={TOURNAMENT_TYPE.EQUIPOS} value={TOURNAMENT_TYPE.EQUIPOS}>Por Equipos</MenuItem>]
              : [
                  <MenuItem key={TOURNAMENT_TYPE.REGULAR_ABIERTO} value={TOURNAMENT_TYPE.REGULAR_ABIERTO}>Regular (Abierto)</MenuItem>,
                  <MenuItem key={TOURNAMENT_TYPE.REGULAR_CERRADO} value={TOURNAMENT_TYPE.REGULAR_CERRADO}>Regular (Cerrado)</MenuItem>,
                  <MenuItem key={TOURNAMENT_TYPE.CLASIFICATORIO} value={TOURNAMENT_TYPE.CLASIFICATORIO}>Clasificatorio</MenuItem>,
                  <MenuItem key={TOURNAMENT_TYPE.CAMPEONES} value={TOURNAMENT_TYPE.CAMPEONES}>Torneo de Campeones</MenuItem>,
                  <MenuItem key={TOURNAMENT_TYPE.ASCENSO} value={TOURNAMENT_TYPE.ASCENSO}>Ascenso</MenuItem>,
                  <MenuItem key={TOURNAMENT_TYPE.CLASIFICATORIO_AFICIONADOS} value={TOURNAMENT_TYPE.CLASIFICATORIO_AFICIONADOS}>Clasificatorio Aficionados</MenuItem>,
                ]}
          </TextField>
        </Grid>

        {/* Sistema */}
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="Sistema"
            name="sistema"
            value={formData.sistema}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            <MenuItem value={TOURNAMENT_SISTEMA.SUIZO}>Suizo</MenuItem>
            <MenuItem value={TOURNAMENT_SISTEMA.AMERICANO}>Americano</MenuItem>
            <MenuItem value={TOURNAMENT_SISTEMA.MATCH}>Match</MenuItem>
          </TextField>
        </Grid>

        {/* Ritmo de Juego */}
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="Ritmo de Juego"
            name="ritmoJuego"
            value={formData.ritmoJuego}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            <MenuItem value={TOURNAMENT_RITMO.PENSADO}>Pensado</MenuItem>
            <MenuItem value={TOURNAMENT_RITMO.RAPIDO}>Rápido</MenuItem>
            <MenuItem value={TOURNAMENT_RITMO.BLITZ}>Blitz</MenuItem>
          </TextField>
        </Grid>

        {/* Número de Rondas */}
        <Grid item xs={12} sm={4}>
          <TextField
            required
            fullWidth
            type="number"
            label="Número de Rondas"
            name="nroRondas"
            value={formData.nroRondas}
            onChange={handleChange}
            error={!!errors.nroRondas}
            helperText={errors.nroRondas}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ min: 1, style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>

        {/* Tipo de Puntaje (solo torneos por equipos) */}
        {type === 'teams' && (
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Tipo de Puntaje"
              name="tipoPuntaje"
              value={formData.tipoPuntaje ?? TOURNAMENT_TIPO_PUNTAJE.MATCH_POINTS}
              onChange={handleChange}
              InputLabelProps={{ style: { color: '#94a3b8' } }}
              SelectProps={{ style: { color: '#cbd5e1' } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <Tooltip
                      title={
                        <Box sx={{ p: 0.5, maxWidth: 260 }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Match Points (2/1/0)</Typography>
                          <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                            El puntaje principal de cada equipo son los puntos de partido: 2 por victoria, 1 por empate, 0 por derrota. Los puntos de tablero se usan como desempate.
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Puntos Individuales</Typography>
                          <Typography variant="caption" display="block">
                            El puntaje principal es la suma de puntos de tablero de todos los jugadores del equipo. Se usa en torneos donde los resultados individuales tienen más peso.
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon sx={{ fontSize: 16, color: '#64748b', cursor: 'help', '&:hover': { color: '#94a3b8' } }} />
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#3a6eb6' },
                  '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                },
              }}
            >
              <MenuItem value={TOURNAMENT_TIPO_PUNTAJE.MATCH_POINTS}>Match Points (2/1/0 por partido)</MenuItem>
              <MenuItem value={TOURNAMENT_TIPO_PUNTAJE.INDIVIDUALES}>Puntos Individuales (suma de tableros)</MenuItem>
            </TextField>
          </Grid>
        )}

        {/* Número de Tableros por Equipo (solo torneos por equipos) */}
        {type === 'teams' && (
          <Grid item xs={12} sm={4}>
            <TextField
              required={type === 'teams'}
              fullWidth
              type="number"
              label="Tableros por Equipo"
              name="nroTableros"
              value={formData.nroTableros ?? 4}
              onChange={handleChange}
              InputLabelProps={{ style: { color: '#94a3b8' } }}
              inputProps={{ min: 1, style: { color: '#cbd5e1' } }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#3a6eb6' },
                  '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                },
              }}
            />
          </Grid>
        )}

        {/* ¿Es Rankeado? */}
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="¿Es Rankeado?"
            name="rankeado"
            value={formData.rankeado}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            <MenuItem value="S">Sí</MenuItem>
            <MenuItem value="N">No</MenuItem>
          </TextField>
        </Grid>

        {/* Estado */}
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label="Estado"
            name="estado"
            value={formData.estado}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            <MenuItem value={TOURNAMENT_ESTADO.CREADO}>Creado</MenuItem>
            <MenuItem value={TOURNAMENT_ESTADO.INSCRIPCION_ABIERTA}>Inscripción Abierta</MenuItem>
            <MenuItem value={TOURNAMENT_ESTADO.INSCRIPCION_CERRADA}>Inscripción Cerrada</MenuItem>
            <MenuItem value={TOURNAMENT_ESTADO.EN_CURSO}>En Curso</MenuItem>
            <MenuItem value={TOURNAMENT_ESTADO.FINALIZADO}>Finalizado</MenuItem>
            <MenuItem value={TOURNAMENT_ESTADO.CANCELADO}>Cancelado</MenuItem>
          </TextField>
        </Grid>

        {/* Software Usado */}
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Software Usado"
            name="softwareUsado"
            value={formData.softwareUsado}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            SelectProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          >
            <MenuItem value={TOURNAMENT_SOFTWARE.MANUAL}>Manual</MenuItem>
            <MenuItem value={TOURNAMENT_SOFTWARE.SWISSPERFECT}>Swissperfect</MenuItem>
            <MenuItem value={TOURNAMENT_SOFTWARE.SWISS_MANAGER}>Swiss Manager</MenuItem>
            <MenuItem value={TOURNAMENT_SOFTWARE.OTRO}>Otro</MenuItem>
          </TextField>
        </Grid>

        {/* Comentarios */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Comentarios"
            name="comentarios"
            value={formData.comentarios}
            onChange={handleChange}
            InputLabelProps={{ style: { color: '#94a3b8' } }}
            inputProps={{ style: { color: '#cbd5e1' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                '&:hover fieldset': { borderColor: '#3a6eb6' },
                '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
              },
            }}
          />
        </Grid>
      </Grid>

      {/* Acciones */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/tournaments')}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              borderColor: '#cbd5e1',
              background: 'rgba(255, 255, 255, 0.05)',
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleNextSubmit}
          disabled={loading}
          endIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
          sx={{
            background: '#3a6eb6',
            '&:hover': { background: '#2a5793' },
          }}
        >
          {torId ? 'Guardar y Siguiente' : 'Siguiente'}
        </Button>
      </Box>
    </Box>
  );
}
